import { group } from "analytics/analytics";
import { segmentTrackAccountLoaded } from "analytics/analyticTrackEvents";
import { useUserDataContext } from "contexts/UserDataContext/UserDataContext";
import { PropsWithChildren, useEffect } from "react";

/**
 * Issues the Segment group() call once user data is available, associating
 * the user with their account. Groups are account-based (the company-level
 * "team" entity), not user-based, so analytics can be rolled up per company.
 *
 * This lives below UserDataProvider (unlike SegmentProvider) because the
 * team is only known after the Hasura user record loads.
 */
export const SegmentGroupProvider: React.FC<PropsWithChildren> = ({
    children
}) => {
    const { teamId, teamName } = useUserDataContext();

    useEffect(() => {
        group(teamId, teamName);
        // Emit immediately after group() so this event is guaranteed to carry
        // the account association. SegmentProvider's first page()/identify() of
        // a cold load can precede the group being set (it fires the instant
        // auth0 resolves, above UserDataProvider), leaving that landing event
        // ungrouped; this gives analytics a company-stamped event to anchor on
        // regardless. It fires on app load and on team change, which is not the
        // same as once per Amplitude session: a reload inside a live session
        // emits a second one, and a session that rolls over while the app stays
        // mounted emits none. Count sessions off session_id, not this event.
        segmentTrackAccountLoaded(teamId, teamName);
    }, [teamId, teamName]);

    return <>{children}</>;
};
